import { faShieldHalved } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    ApplicationRoleCode,
    CorporationJoinMode,
    IUserResponse,
    KcfHandling,
    LocationJoinMode,
} from '@kcftech/prometheus-client';
import {
    AutoCompleteInput,
    AutoCompleteRenderInfo,
} from 'components/data/AutoCompleteInput';
import { CheckedAutoCompleteResult } from 'components/data/CheckedAutoCompleteResult';
import {
    CurrentUsers,
    EmailColumn,
    FirstNameColumn,
    LastNameColumn,
    UsersGridContainer,
    UsersHeader,
    UsersInfoList,
} from 'components/users/styled';
import { UsersList } from 'components/users/UsersList';
import { usePermissions } from 'contexts/PermissionsContext';
import {
    UsersFilter,
    useInfiniteUsersDataSource,
} from 'custom_hooks/data_hooks/data_source/usersDataSource';
import { useValueAsTuple } from 'custom_hooks/useValueAsTuple';
import { isKcfUser } from 'pages/Desk/issues/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SecondaryText } from 'styles/theme_styles/text';
import { tokens } from 'styles/tokens';
import { sortBy, uniqueBy } from 'utils/array';

export type UserInfo = Pick<
    IUserResponse,
    'Id' | 'Email' | 'FirstName' | 'LastName' | 'ApplicationRoleCode'
> & {
    error?: string;
};

const userResultKey = ({ Id, FirstName, LastName }: UserInfo) =>
    `${Id}-${FirstName}-${LastName}`;

/**
 * The values in the enum solely affect how Corporation<->User and
 * Location<->User relationships are used when filtering.
 */
export enum UserRoleFilterMode {
    /**
     * Include all users that have corporation or location roles that grant them
     * admin rights at the specified corporation or location
     */
    CanAdmin,
    /**
     * Include all users that have corporation or location roles that grant them
     * view rights at the specified corporation or location
     */
    CanView,
    /**
     * Include all users with an explicit role at the corporation or location.
     * (independent of their functional permissions)
     */
    ExplicitRole,
}

type FilteringOptions = Readonly<{
    corporationId?: string;
    locationId?: string;
    userRoleFilterMode?: UserRoleFilterMode;
    kcfHandling?: KcfHandling;
    applicationRoleCodes?: ApplicationRoleCode[];
    /**
     * if `true`, includes only users with an assignment at a corporation where
     * the requester user has admin rights
     */
    whereAdmin?: boolean;
}>;

const userRoleFilterModeToJoinModes = (
    filterMode: UserRoleFilterMode | undefined,
): [CorporationJoinMode, LocationJoinMode] => {
    switch (filterMode) {
        case UserRoleFilterMode.CanAdmin:
            return [CorporationJoinMode.CanAdminAt, LocationJoinMode.CanAdminAt];
        case UserRoleFilterMode.CanView:
            return [CorporationJoinMode.CanViewAt, LocationJoinMode.CanViewAt];
        case UserRoleFilterMode.ExplicitRole:
        default:
            return [CorporationJoinMode.ExplicitRole, LocationJoinMode.ExplicitRole];
    }
};

const useQueryFilterFromOptions = ({
    userRoleFilterMode,
    kcfHandling,
    applicationRoleCodes,
}: FilteringOptions): UsersFilter => {
    const { canViewKcfUsers } = usePermissions();

    return useMemo(() => {
        const [corporationJoinMode, locationJoinMode] =
            userRoleFilterModeToJoinModes(userRoleFilterMode);

        return {
            kcfHandling:
                (canViewKcfUsers ? kcfHandling : KcfHandling.Hide) ?? KcfHandling.Default,
            corporationJoinMode,
            locationJoinMode,
            applicationRoleCodes,
        };
    }, [userRoleFilterMode, canViewKcfUsers, kcfHandling, applicationRoleCodes]);
};

export type UsersInputProps<TUserInfo extends UserInfo> = Readonly<{
    value: TUserInfo[];
    filterText: string;
    filterPlaceholder?: string;
    maxHeight?: string;
    onFilterTextChange: (newFilterText: string) => void;
    /**
     * note: cached on mount
     */
    userResponseToUserInfo: (res: IUserResponse) => TUserInfo;
    onChange: (newUsers: TUserInfo[]) => void;
    disabled?: boolean;
    title?: string;
}> &
    FilteringOptions;

export function UsersInput<TUserInfo extends UserInfo>({
    corporationId,
    locationId,
    userRoleFilterMode,
    kcfHandling,
    applicationRoleCodes,
    userResponseToUserInfo: _userResponseToUserInfo,
    whereAdmin,
    value,
    onChange,
    filterText,
    onFilterTextChange,
    filterPlaceholder = 'Search users',
    maxHeight = '40rem',
    disabled,
    title,
}: UsersInputProps<TUserInfo>) {
    const userResponseToUserInfoRef = useRef(_userResponseToUserInfo);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const onFilterTextChangeRef = useRef(onFilterTextChange);
    onFilterTextChangeRef.current = onFilterTextChange;

    const corporationIds = useValueAsTuple(corporationId);
    const locationIds = useValueAsTuple(locationId);

    const {
        results,
        setFilter: setResultsFilter,
        isLoading,
        hasNext,
        loadNext,
    } = useInfiniteUsersDataSource({
        name: 'users-list',
        defaultFilter: {
            ...useQueryFilterFromOptions({
                userRoleFilterMode,
                kcfHandling,
                applicationRoleCodes,
            }),
            corporationIds,
            locationIds,
            whereAdmin,
            nameSearch: filterText,
        },
        enabled: isPopupOpen,
    });

    const currentUserIds = useMemo(() => new Set(value.map((user) => user.Id)), [value]);

    const sortedAndUniqueUsers = useMemo(
        () =>
            sortBy(
                uniqueBy(value, (user) => user.Id),
                (user) => user.FirstName.toLowerCase(),
            ),
        [value],
    );

    const usersToRender = useMemo<TUserInfo[]>(() => {
        const userNames = sortBy(
            uniqueBy(results ?? [], (user) => user.Id),
            (user) => user.FirstName.toLowerCase(),
        ).map(userResponseToUserInfoRef.current);
        return userNames;
    }, [results]);

    const toggleUser = useCallback(
        (userToToggle: TUserInfo) => {
            if (currentUserIds.has(userToToggle.Id)) {
                onChangeRef.current(value.filter((user) => user.Id !== userToToggle.Id));
            } else {
                onChangeRef.current([...value, userToToggle]);
            }
        },
        [currentUserIds, value],
    );

    const renderUser = useCallback(
        ({
            item,
            isHighlighted,
            resultRef,
            onResultSelect,
        }: AutoCompleteRenderInfo<TUserInfo>) => (
            <CheckedAutoCompleteResult
                label={
                    <div>
                        <div>
                            {item.FirstName} {item.LastName}
                            {isKcfUser(item.ApplicationRoleCode) && (
                                <FontAwesomeIcon
                                    icon={faShieldHalved}
                                    className="f-14 ml-10"
                                />
                            )}
                        </div>
                        <SecondaryText>{item.Email}</SecondaryText>
                    </div>
                }
                $isHighlighted={isHighlighted}
                isChecked={currentUserIds.has(item.Id)}
                onResultSelect={onResultSelect}
                ref={resultRef}
            />
        ),
        [currentUserIds],
    );

    useEffect(() => {
        setResultsFilter((current) => ({
            ...current,
            locationIds,
            nameSearch: filterText,
        }));
        setHighlightedIndex(0);
    }, [filterText, locationIds, setResultsFilter]);

    return (
        <>
            <AutoCompleteInput<TUserInfo>
                title={title}
                filterText={filterText}
                onFilterTextChange={onFilterTextChange}
                filterPlaceholder={filterPlaceholder}
                isPopupOpen={isPopupOpen}
                onPopupChange={setIsPopupOpen}
                results={usersToRender}
                resultKey={userResultKey}
                renderResult={renderUser}
                highlightedIndex={highlightedIndex}
                onHighlightedIndexChange={setHighlightedIndex}
                popupClassName="modal-popup"
                onSelect={toggleUser}
                dataSource={{
                    hasNext,
                    isLoading,
                    loadNext,
                }}
                isDisabled={disabled}
            />
            {sortedAndUniqueUsers.length > 0 && (
                <CurrentUsers>
                    <UsersGridContainer>
                        <UsersHeader $color={tokens.text.primary}>
                            <FirstNameColumn>First Name</FirstNameColumn>
                            <LastNameColumn>Last Name</LastNameColumn>
                            <EmailColumn>Email</EmailColumn>
                        </UsersHeader>
                        <UsersInfoList $maxHeight={maxHeight}>
                            {sortedAndUniqueUsers.map((user) => (
                                <div key={userResultKey(user)}>
                                    <UsersList
                                        color={tokens.text.primary}
                                        onDelete={() => toggleUser(user)}
                                        user={{
                                            UserId: user.Id,
                                            UserFirstName: user.FirstName,
                                            UserLastName: user.LastName,
                                            UserEmail: user.Email,
                                            UserApplicationRole: user.ApplicationRoleCode,
                                            error: user.error,
                                        }}
                                        disabled={disabled}
                                    />
                                </div>
                            ))}
                        </UsersInfoList>
                    </UsersGridContainer>
                </CurrentUsers>
            )}
        </>
    );
}
